import { t } from "@lingui/macro";

import { EventChargePayerRowData } from "@whoosh/design-system/src/components/display/ChargePayerRow/ChargePayerRow";

import { EventSignUpRow } from "../types";

type getEventPrimaryChargePaymentDataProps = {
  currentUserMemberId: string | null;
  primaryChargeIdToPriceMap?: { id: string | null; price: number }[];
  currentSignUpRow: EventSignUpRow | null;
  memberSignUpProxyAllowed?: boolean;
};

export const getEventPrimaryChargePaymentData = ({
  currentUserMemberId,
  primaryChargeIdToPriceMap,
  currentSignUpRow,
  memberSignUpProxyAllowed,
}: getEventPrimaryChargePaymentDataProps) => {
  const getPrice = (id: string | null) => {
    return primaryChargeIdToPriceMap?.find((pm) => pm.id === id)?.price ?? 0;
  };
  if (!currentSignUpRow) return [];

  //Get all the rows that are paying for themselves.
  const onlySelfPayers: EventChargePayerRowData[] =
    currentSignUpRow.registrations
      .filter((cur) => {
        const isMemberOrCustomer =
          cur.type === "MEMBER" || cur.type === "CUSTOMER";
        const isPayingForSelf =
          isMemberOrCustomer &&
          (cur.payingMemberId === cur.registrant.playerId ||
            (cur.type === "CUSTOMER" &&
              cur.payingCustomerId === cur.registrant.id));
        return isPayingForSelf;
      })
      .flatMap((cur) => {
        return cur.registrant.playerId &&
          cur.registrant.firstName &&
          cur.registrant.lastName
          ? [
              {
                payer: {
                  firstName: cur.registrant.firstName,
                  lastName: cur.registrant.lastName,
                  id: cur.registrant.playerId,
                  price: getPrice(cur.primaryRegistrationChargeTypeId),
                  profileImageId: cur.registrant.profileImage?.publicId,
                  detail: cur.registrant.detail,
                  type: cur.type ? cur.type : "MEMBER",
                  isCurrentUser:
                    cur.registrant.playerId === currentUserMemberId,
                  displayName: cur.primaryRegistrationChargeTypeDisplayName,
                  displayDescription:
                    cur.primaryRegistrationChargeTypeDisplayDescription,
                  unknownRegistrantDisplayName:
                    cur.unknownRegistrantDisplayName,
                },
                additionalChargeRows: [],
              },
            ]
          : [];
      });

  const isOwnerInRegistrations = currentSignUpRow.registrations.some(
    (r) => r.registrant.playerId === currentSignUpRow.ownerClient?.id
  );

  if (
    memberSignUpProxyAllowed &&
    currentSignUpRow.ownerClient &&
    !isOwnerInRegistrations
  ) {
    onlySelfPayers.unshift({
      payer: {
        firstName: currentSignUpRow.ownerClient.firstName,
        lastName: currentSignUpRow.ownerClient.lastName,
        id: currentSignUpRow.ownerClient.id || "",
        price: 0,
        profileImageId: currentSignUpRow.ownerClient.profileImagePublicId,
        detail: undefined,
        type: "MEMBER",
        isCurrentUser: currentSignUpRow.ownerClient.id === currentUserMemberId,
        displayName: null,
        displayDescription: null,
      },
      additionalChargeRows: [],
    });
  }

  const selfPayerIdList = onlySelfPayers.map((cur) => cur.payer.id);
  //Add anyone else that is being payed for beneath them.
  //Default anyone without a payer ID to be paid for by the first row.
  let payedForIdList: string[] = [];

  const chargeData: EventChargePayerRowData[] = onlySelfPayers.map((cur) => {
    const payerId = cur.payer.id;
    const additionalChargeRows = currentSignUpRow.registrations
      .filter((cur) => {
        const isMember = cur.type === "MEMBER";
        const isCustomer = cur.type === "CUSTOMER";
        const isPayingForSelf =
          (isMember && cur.payingMemberId === cur.registrant.playerId) ||
          (isCustomer && cur.payingCustomerId === cur.registrant.id);

        const isAlreadyPayedFor =
          cur.registrant.playerId &&
          payedForIdList.includes(cur.registrant.playerId);

        const isUnknownGuest = cur.type === "GUEST" && !cur.registrant.playerId;

        // Check if this customer is being paid for by this payer
        const customerPaidByThisPayer =
          isCustomer && cur.payingCustomerId === payerId;

        // For customers with a payingCustomerId set, only include them if that customer is this payer
        // Don't default unknown customers to the first member if they have a payingCustomerId
        const hasSpecificCustomerPayer =
          isCustomer &&
          cur.payingCustomerId &&
          cur.payingCustomerId !== payerId;

        return (
          !isUnknownGuest &&
          !isAlreadyPayedFor &&
          !isPayingForSelf &&
          !hasSpecificCustomerPayer &&
          (cur.payingMemberId === payerId ||
            customerPaidByThisPayer ||
            !cur.payingMemberId ||
            !selfPayerIdList.includes(cur.payingMemberId))
        );
      })
      .flatMap((cur) => {
        return [
          {
            firstName: cur.registrant.firstName ?? t`Unknown`,
            lastName: cur.registrant.lastName ?? t`Player`,
            id: cur.registrant.playerId || "",
            price: getPrice(cur.primaryRegistrationChargeTypeId),
            profileImageId: cur.registrant.profileImage?.publicId,
            detail: cur.registrant.detail,
            type: cur.type ? cur.type : "MEMBER",
            payingMemberId: cur.payingMemberId || "",
            payingCustomerId: cur.payingCustomerId || "",
            displayName: cur.primaryRegistrationChargeTypeDisplayName,
            unknownRegistrantDisplayName: cur.unknownRegistrantDisplayName,
            displayDescription:
              cur.primaryRegistrationChargeTypeDisplayDescription,
          },
        ];
      });
    additionalChargeRows.forEach((cur) => {
      payedForIdList.push(cur.id || "");
    });
    return {
      payer: cur.payer,
      additionalChargeRows,
    };
  });

  return chargeData;
};
